import { NODE_TYPE } from 'edrolo/domains/bySchoolClass';
import PT from 'prop-types';

/*
 * Renders a course assemblage node and its children.
 * Node rendering is controlled using the appropriate render prop for each node type supported by the assemblage.
 *
 * @param node - the assemblage node to render
 * @param assemblage - the full assemblage object
 * @param depth - the depth of the node in the assemblage tree. This can be passed to render props to control rendering based on depth.
 * @param depthLimit - the maximum depth to render. Nodes deeper than this will not be rendered.
 * @param renderCourse - a function that renders a course node.
 * @param renderSection - a function that renders a section node.
 * @param renderLesson - a function that renders a lesson node.
 * @param renderExam - a function that renders an exam node.
 */
export const AssemblageNode = ({
  node,
  assemblage,
  depth,
  depthLimit,
  renderCourse,
  renderSection,
  renderLesson,
  renderExam,
}) => {
  if (!node) {
    return null;
  }

  if (depthLimit && depth > depthLimit) {
    return null;
  }

  const nodeType = node.node_type;
  const nodeChildren = (node.children || []).map((n, i) => (
    <AssemblageNode
      key={i}
      node={n}
      depth={depth + 1}
      depthLimit={depthLimit}
      assemblage={assemblage}
      renderCourse={renderCourse}
      renderSection={renderSection}
      renderLesson={renderLesson}
      renderExam={renderExam}
    />
  ));

  if (nodeType === NODE_TYPE.COURSE) {
    const course = assemblage.course;
    return renderCourse(course, nodeChildren);
  }

  if (nodeType === NODE_TYPE.SECTION) {
    const section = assemblage.content.sections[node.value_id];
    return renderSection(section, depth, nodeChildren);
  }

  if (nodeType === NODE_TYPE.LESSON) {
    const lesson = assemblage.content.lessons[node.value_id];
    return renderLesson(lesson);
  }

  if (nodeType === NODE_TYPE.EXAM) {
    const exam = assemblage.content.exams[node.value_id];
    return renderExam(exam);
  }

  return null;
};

AssemblageNode.propTypes = {
  node: PT.object.isRequired,
  assemblage: PT.object.isRequired,
  depth: PT.number,
  depthLimit: PT.number,
  renderCourse: PT.func,
  renderSection: PT.func,
  renderLesson: PT.func,
  renderExam: PT.func,
};

AssemblageNode.defaultProps = {
  depth: 0,
  depthLimit: Infinity, // TODO provide a more sensible default?
  renderCourse: () => <></>,
  renderSection: () => <></>,
  renderLesson: () => <></>,
  renderExam: () => <></>,
};
